import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Search} from 'lucide-react';
import useApi from '../../utils/useApi';
import styles from './Search.module.css';
import {IoArrowBackCircleOutline} from "react-icons/io5";
import AnimatedSearchIndicator from "../Icons/AnimatedSearchIndicator/AnimatedSearchIndicator";
import BusinessList from "../BusinessList/BusinessList";
import ProductList from "../ProductList/ProductList";
import {useVoiceSearch} from '../VoiceGPT/VoiceSearchContext';
import {useTheme} from "../ThemeContext/ThemeContext";
import LottieIcon from "../LottieIcon/LottieIcon";

const SearchApi = ({ handleShowHeader, handleShowSettingsHeader, handleShowDate, geoposition }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchInputRef = useRef(null);

    const { isDarkMode } = useTheme();  // Use the useTheme hook
    const [initial, setInitial] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('All');
    const [searchResults, setSearchResults] = useState({});
    const { searchApi, isLoading, error } = useApi();
    const [isSearching, setIsSearching] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { transcript, shouldSearch, resetSearch } = useVoiceSearch();
    const [prevSearchTerms, setPrevSearchTerms] = useState([]);

    const performSearch = useCallback(async (term, tab, initialSearch) => {
        if (term.length > 2 || initialSearch) {
            setIsSearching(true);
            try {
                let results;
                if(term.length > 2){
                    handleChangeUrl(term);
                }
                if (tab === 'All') {
                    results = await searchApi(geoposition, term, 'all-search');
                } else {
                    results = await searchApi(geoposition, term, tab.toLowerCase());
                }
                setSearchResults(results);

                if (term.length > 2) {
                    // Save search term to local storage
                    const updatedTerms = [term, ...prevSearchTerms.filter(t => t !== term)].slice(0, 6);
                    localStorage.setItem('prevSearchTerms', JSON.stringify(updatedTerms));
                    setPrevSearchTerms(updatedTerms);
                }
                setIsSearching(false);

            }
            catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Search failed:', err);
                }
            }
            finally {
               // setIsSearching(false);
            }
        } else {
            setSearchResults({});
        }
    }, [searchApi, geoposition, prevSearchTerms]);

    // Initial URL query check and local storage load
    useEffect(() => {
        setInitial(false);
        const query = searchParams.get('q');

        console.log(query);
        if (query) {
            setSearchTerm(query);
            setDebouncedSearchTerm(query);
            performSearch(query, activeTab);
        } else {
            // Load last search term from local storage if no query in URL
            const storedTerms = JSON.parse(localStorage.getItem('prevSearchTerms') || '[]');
            if (storedTerms.length > 0) {
                setSearchTerm(storedTerms[0]);
                setDebouncedSearchTerm(storedTerms[0]);
                performSearch(storedTerms[0], activeTab);
            } else {
                let initialSearch = true;
                performSearch(geoposition.city, activeTab, initialSearch);
            }
            setPrevSearchTerms(storedTerms.slice(0, 6));
        }
    }, []);

    useEffect(() => {
        if(window.innerWidth >= 778){
            handleShowHeader(true);
            handleShowDate(false);
        } else {
            handleShowHeader(false);
        }
        handleShowSettingsHeader(false);
    }, [handleShowHeader, handleShowSettingsHeader]);

    useEffect(() => {
        if (shouldSearch && transcript && !initial) {
            console.log('SHOULD SEARCH');
            setDebouncedSearchTerm(transcript);
            performSearch(transcript, activeTab);
            resetSearch();
        }
    }, [shouldSearch, activeTab]);

    // Debounce effect
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Perform search when debounced search term changes
    useEffect(() => {
        performSearch(debouncedSearchTerm, activeTab);
    }, [debouncedSearchTerm, activeTab]);

    const handleChangeUrl = (term) => {
        setSearchParams({ ...searchParams, q: term });
    }

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        navigate(`/search-all?q=${encodeURIComponent(searchTerm)}`);
    };

    const handleHistoryItemClick = (term) => {
        setSearchTerm(term);
        setDebouncedSearchTerm(term);
        performSearch(term, activeTab);
    };

    const clearSearch = () => {
        // Clear the search term
        setSearchTerm('');

        // Optional: refocus the input after clearing
        // You'd need to add a ref to your input for this
        if (searchInputRef.current) searchInputRef.current.focus();
    };

    const tabs = ['All', 'Deals', 'Products', 'Locations'];

    const renderSearchHistory = () => {
        if (prevSearchTerms.length === 0) return null;

        return (
            <div className={styles.categorySection}>
                <h2 className={styles.categoryTitle}>Recent Searches</h2>
                <ul className={styles.resultsList}>
                    {prevSearchTerms.map((term, index) => (
                        <li key={index} className={styles.resultItem}>
                            <AnimatedSearchIndicator customClass={styles.searchIndicator}/>
                            <button onClick={() => handleHistoryItemClick(term)} className={styles.historyItem}>
                                {term}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderSearchResults = () => {
        if (activeTab === 'All') {
            return (
                <>
                    {renderSearchHistory()}

                    {(isSearching || isLoading) && (
                        <>
                            <p className={styles.message}>Loading...</p>
                            <LottieIcon width={125} height={40} variant={'loading'} speed={0.63} className={styles.lottieSvg}/>
                        </>
                    )}
                    {searchResults.deals?.data?.length > 0 && (
                        <div className={styles.categorySection}>
                            <div className={`${styles.titleWrap} flex flex-row space-between`}>
                                <h2 className={styles.categoryTitle}>Deals</h2>

                            </div>
                            <ProductList products={searchResults.deals.data.slice(0, 3)}/>
                            <div className={`${styles.titleWrap} flex flex-row justify-center`}>
                                <h2 className={styles.viewAllBtn} onClick={() => handleTabClick('Deals')}>View All Deals</h2>
                            </div>
                        </div>
                    )}
                    {searchResults.products?.data?.length > 0 && (
                        <div className={styles.categorySection}>
                            <div className={`${styles.titleWrap} flex flex-row space-between`}>

                                <h2 className={styles.categoryTitle}>Products</h2>

                            </div>
                            <ProductList products={searchResults.products.data.slice(0, 3)}
                                         isLoading={isSearching}
                            />
                            <div className={`${styles.titleWrap} flex flex-row justify-center`}>
                                <h2 className={styles.viewAllBtn} onClick={() => handleTabClick('Products')}>View All Products</h2>
                            </div>
                        </div>
                    )}
                    {searchResults.locations?.data?.length > 0 && (
                        <div className={styles.categorySection}>
                            <div className={`${styles.titleWrap} flex flex-row space-between`}>

                                <h2 className={styles.categoryTitle}>Locations</h2>
                                <h2 className={styles.viewAllBtn} onClick={() => handleTabClick('Locations')}>View All</h2>
                            </div>
                            <BusinessList businesses={searchResults.locations.data.slice(0, 3)} loading={isSearching}/>
                        </div>
                    )}
                </>
            );
        } else if (activeTab === 'Products') {
            return searchResults.products?.data?.length ? (
                <ProductList products={searchResults.products.data} isLoading={isSearching}/>
            ) : (
                       <p className={styles.message}>No products found. Try modifying your search or changing your location.</p>
                   );
        } else if (activeTab === 'Locations') {
            return searchResults.locations?.data?.length ? (
                <BusinessList businesses={searchResults.locations.data} loading={isSearching}/>
            ) : (
                       <p className={styles.message}>No locations found. Try modifying your search or changing your location.</p>
                   );
        } else if (activeTab === 'Deals') {
            return searchResults.deals?.data?.length ? (
                <ProductList products={searchResults.deals.data}/>
            ) : (
                       <p className={styles.message}>No deals found. Try modifying your search or changing your location.</p>
                   );
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchInputWrapper}>
                <div className={styles.backArrow}>
                    <button onClick={() => navigate('/')} className={styles.backButton}>
                        <IoArrowBackCircleOutline/>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className={styles.searchInputContainer}>
                    <AnimatedSearchIndicator customClass={styles.searchIndicator}/>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        placeholder="Search..."
                        className={styles.searchInput}
                    />

                    {searchTerm && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-[78px] top-0 bottom-0 m-auto flex items-center transform -translate-y-1/2 text-gray-500 hover:text-gray-700 bg-transparent border-0"
                            aria-label="Clear search"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    )}

                    <button type="submit" className={styles.searchButton}>
                        {isDarkMode ? (
                            <>
                                {isSearching ? <AnimatedSearchIndicator/> : <Search size={24} color="white"/>}

                            </>
                        ) : (
                             <>
                                 {isSearching ? <AnimatedSearchIndicator/> : <Search size={24} color="white"/>}
                             </>
                         )}
                    </button>
                </form>
            </div>

            <div className={styles.tabsContainer}>
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        onClick={() => handleTabClick(tab)}
                        className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
                    >
                        {tab}
                    </button>
                ))}
            </div>



            {isLoading ? (
                <>
                    <p className={styles.message}>Loading...</p>
                    {(isSearching || isLoading) && (
                        <LottieIcon width={125} height={40} variant={'loading'} speed={0.63} className={styles.lottieSvg}/>
                    )}
                </>

            ) : error ? (
                <p className={styles.message}>Error: {error}</p>
            ) : (
                    <div className={styles.resultsContainer}>{renderSearchResults()}</div>
                )}
        </div>
    );
};

export default SearchApi;
